import { useState } from "react";
import { 
  Phone, 
  Envelope, 
  MapPin, 
  WhatsappLogo,
  PaperPlaneTilt,
  User,
  Buildings,
  ChatText
} from "@phosphor-icons/react";

const contactInfo = [
  {
    icon: User,
    title: "Gerencia Comercial",
    name: "Víctor A. Pinto Marconi",
    phone: "323 323 0210",
    email: "victor.pinto@seinmarc.com"
  },
  {
    icon: Buildings,
    title: "Operaciones",
    name: "Fabián Pinto",
    phone: "311 251 5236",
    email: "fabian.pinto@seinmarc.com"
  }
];

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Crear mensaje para WhatsApp
    const whatsappMessage = `*Nueva Solicitud de Cotización - SEINMAR*%0A%0A` +
      `*Nombre:* ${formData.name}%0A` +
      `*Empresa:* ${formData.company || 'No especificada'}%0A` +
      `*Email:* ${formData.email}%0A` +
      `*Teléfono:* ${formData.phone}%0A` +
      `*Servicio:* ${formData.service || 'No especificado'}%0A` +
      `*Mensaje:* ${formData.message}`;

    // Abrir WhatsApp con el mensaje
    const whatsappUrl = `https://wa.me/573233230210?text=${whatsappMessage}`;
    window.open(whatsappUrl, '_blank');

    setSubmitStatus("success");
    setFormData({
      name: "",
      company: "",
      email: "",
      phone: "",
      service: "",
      message: ""
    });
    setIsSubmitting(false);
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="section-padding bg-slate-50 relative overflow-hidden"
    >
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-12 h-0.5 bg-[#C92127]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Contacto
            </span>
            <span className="w-12 h-0.5 bg-[#C92127]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">
            ¿Listo para <span className="text-[#C92127]">comenzar</span>?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Contáctenos para una cotización sin compromiso. Estamos listos para 
            ser su aliado estratégico en obra civil y mantenimiento industrial.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            {contactInfo.map((contact, index) => {
              const Icon = contact.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#C92127]/10 flex items-center justify-center">
                      <Icon size={20} weight="duotone" className="text-[#C92127]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500">{contact.title}</p>
                      <p className="font-bold text-slate-900">{contact.name}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <a
                      href={`tel:+57${contact.phone.replace(/\s/g, "")}`}
                      className="flex items-center gap-3 text-slate-600 hover:text-[#C92127] transition-colors"
                    >
                      <Phone size={18} weight="duotone" />
                      <span>{contact.phone}</span>
                    </a>
                    <a
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-3 text-slate-600 hover:text-[#C92127] transition-colors"
                    >
                      <Envelope size={18} weight="duotone" />
                      <span>{contact.email}</span>
                    </a>
                  </div>
                </div>
              );
            })}

            {/* Location */}
            <div className="bg-white p-6 rounded-xl border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                  <MapPin size={20} weight="duotone" className="text-[#1E3A8A]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Ubicación</p>
                  <p className="font-bold text-slate-900">Cali, Valle del Cauca</p>
                </div>
              </div>
              <p className="text-slate-600">Colombia</p>
            </div>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/573233230210?text=Hola,%20me%20interesa%20conocer%20más%20sobre%20sus%20servicios"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 bg-[#25D366] text-white p-4 rounded-xl font-bold hover:bg-[#128C7E] transition-colors"
              data-testid="whatsapp-cta"
            >
              <WhatsappLogo size={24} weight="fill" />
              Escríbenos por WhatsApp
            </a>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="bg-white p-8 md:p-10 rounded-2xl border border-slate-200 shadow-xl"
              data-testid="contact-form"
            >
              <div className="flex items-center gap-3 mb-8">
                <ChatText size={24} weight="duotone" className="text-[#C92127]" />
                <h3 className="text-2xl font-bold text-slate-900">Solicitar Cotización</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#C92127] focus:ring-2 focus:ring-[#C92127]/20 outline-none transition-all"
                    placeholder="Su nombre"
                    data-testid="input-name"
                  />
                </div>

                {/* Company */}
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-slate-700 mb-2">
                    Empresa
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#C92127] focus:ring-2 focus:ring-[#C92127]/20 outline-none transition-all"
                    placeholder="Nombre de la empresa"
                    data-testid="input-company"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    Correo electrónico *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#C92127] focus:ring-2 focus:ring-[#C92127]/20 outline-none transition-all"
                    placeholder="correo@ejemplo.com"
                    data-testid="input-email"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#C92127] focus:ring-2 focus:ring-[#C92127]/20 outline-none transition-all"
                    placeholder="300 000 0000"
                    data-testid="input-phone"
                  />
                </div>

                {/* Service */}
                <div className="md:col-span-2">
                  <label htmlFor="service" className="block text-sm font-semibold text-slate-700 mb-2">
                    Servicio de interés
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#C92127] focus:ring-2 focus:ring-[#C92127]/20 outline-none transition-all bg-white"
                    data-testid="select-service"
                  >
                    <option value="">Seleccione un servicio</option>
                    <option value="Obra Civil y Adecuaciones Industriales">Obra Civil y Adecuaciones Industriales</option>
                    <option value="Mantenimiento Locativo Integral">Mantenimiento Locativo Integral</option>
                    <option value="Pisos Industriales y Recubrimientos">Pisos Industriales y Recubrimientos</option>
                    <option value="Servicios de Apoyo Operativo">Servicios de Apoyo Operativo</option>
                    <option value="Instalaciones Técnicas">Instalaciones Técnicas</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                {/* Message */}
                <div className="md:col-span-2">
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#C92127] focus:ring-2 focus:ring-[#C92127]/20 outline-none transition-all resize-none"
                    placeholder="Cuéntenos sobre su proyecto o necesidad..."
                    data-testid="input-message"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="mt-8">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    "Enviando..."
                  ) : (
                    <>
                      <PaperPlaneTilt size={20} weight="bold" />
                      Enviar por WhatsApp
                    </>
                  )}
                </button>

                {submitStatus === "success" && (
                  <p className="mt-4 text-center text-green-600 font-medium" data-testid="success-message">
                    ¡Se abrió WhatsApp con tu mensaje! Envíalo para completar la solicitud.
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactSection;
